import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const PINTRYX_VARIANTS = {
  premium95silver: {
    default: 45065864446022,
    two: 45204116111430,
    three: 45199554019398,
  },
  essential95black: {
    default: 45065860579398,
    two: 45204116340806,
    three: 45199554248774,
  },
  essential95red: {
    default: 45065860710470,
    two: 45204116373574,
    three: 45199554281542,
  },
  essential78black: {
    default: 45065860612166,
    two: 45204116439110,
    three: 45199554347078,
  },
};

const PINTRYX_VARIANT_LOOKUP = new Map();
for (const [family, routes] of Object.entries(PINTRYX_VARIANTS)) {
  for (const [tier, variantId] of Object.entries(routes)) {
    PINTRYX_VARIANT_LOOKUP.set(Number(variantId), { family, tier });
  }
}

/**
 * Rebuilds Pintryx pair lines after a cart edit so their product identities always
 * match the remaining quantity while assigning the available discounts to the
 * highest-priced families: 1 = default, 2 = one MSRP40,
 * 3+ = one MSRP40 + MSRP50 additional pairs.
 * @param {any} cart
 * @param {string[]} sections
 * @returns {Promise<any>}
 */
async function normalizePintryxPairLines(cart, sections) {
  if (!cart?.items?.length) return cart;

  const pairLines = cart.items.filter((item) =>
    PINTRYX_VARIANT_LOOKUP.has(Number(item.variant_id))
  );

  if (!pairLines.length) return cart;

  const tierRank = { default: 0, two: 1, three: 2 };

  /*
   * Expand every cart line into individual logical units.
   *
   * New bundles carry:
   *   _Bundle batch = when that bundle was added
   *   _Bundle order = the first logical pair position in that line
   *
   * A line with quantity > 1 therefore represents consecutive positions.
   */
  const units = [];

  pairLines.forEach((line, lineIndex) => {
    const route = PINTRYX_VARIANT_LOOKUP.get(Number(line.variant_id));
    if (!route) return;

    const properties = line.properties || {};

    const rawBatch = properties['_Bundle batch'];
    const batch =
      rawBatch !== undefined && rawBatch !== null
        ? String(rawBatch)
        : '';

    const rawOrder = properties['_Bundle order'];
    const parsedOrder =
      rawOrder !== undefined &&
      rawOrder !== null &&
      rawOrder !== ''
        ? Number(rawOrder)
        : null;

    const baseOrder =
      Number.isFinite(parsedOrder) && parsedOrder > 0
        ? parsedOrder
        : null;

    const quantity = Number(line.quantity || 0);

    for (let unitIndex = 0; unitIndex < quantity; unitIndex += 1) {
      units.push({
        family: route.family,
        currentTier: route.tier,
        batch,
        order: baseOrder === null ? null : baseOrder + unitIndex,
        lineIndex,
        unitIndex,
      });
    }
  });

  if (!units.length) return cart;

  /*
   * Preserve the customer's original logical order.
   *
   * Legacy lines without metadata come first and fall back to the old
   * default/two/three ordering.
   *
   * New lines are ordered by bundle batch, then by their saved position.
   */
  units.sort((a, b) => {
    const aHasBatch = !!a.batch;
    const bHasBatch = !!b.batch;

    if (aHasBatch !== bHasBatch) {
      return aHasBatch ? 1 : -1;
    }

    if (aHasBatch && bHasBatch && a.batch !== b.batch) {
      const aBatchNumber = Number(a.batch);
      const bBatchNumber = Number(b.batch);

      const aBatchIsNumber = Number.isFinite(aBatchNumber);
      const bBatchIsNumber = Number.isFinite(bBatchNumber);

      if (
        aBatchIsNumber &&
        bBatchIsNumber &&
        aBatchNumber !== bBatchNumber
      ) {
        return aBatchNumber - bBatchNumber;
      }

      return a.batch.localeCompare(b.batch);
    }

    const aHasOrder = Number.isFinite(a.order);
    const bHasOrder = Number.isFinite(b.order);

    if (aHasOrder && bHasOrder && a.order !== b.order) {
      return a.order - b.order;
    }

    if (aHasOrder !== bHasOrder) {
      return aHasOrder ? -1 : 1;
    }

    const tierDifference =
      tierRank[a.currentTier] - tierRank[b.currentTier];

    if (tierDifference !== 0) return tierDifference;
    if (a.lineIndex !== b.lineIndex) return a.lineIndex - b.lineIndex;

    return a.unitIndex - b.unitIndex;
  });

  const totalPairs = units.length;

  /*
   * The Shopify rules require exactly one Default line and, for bundles,
   * one MSRP40 line plus MSRP50 lines for every remaining pair. Assign those
   * routes by product value so Premium receives the strongest discount first.
   */
  const familyPriceRank = {
    premium95silver: 2,
    essential95black: 1,
    essential95red: 1,
    essential78black: 1,
  };

  const desiredTiers = Array(totalPairs).fill('default');
  const pricingPriority = units
    .map((unit, index) => ({ unit, index }))
    .sort((a, b) => {
      const priceDifference =
        (familyPriceRank[b.unit.family] || 0) -
        (familyPriceRank[a.unit.family] || 0);

      return priceDifference || b.index - a.index;
    });

  if (totalPairs === 2) {
    desiredTiers[pricingPriority[0].index] = 'two';
  } else if (totalPairs >= 3) {
    for (let index = 0; index < totalPairs - 2; index += 1) {
      desiredTiers[pricingPriority[index].index] = 'three';
    }
    desiredTiers[pricingPriority[totalPairs - 2].index] = 'two';
  }

  const normalizedBatch =
    units.find((unit) => unit.batch)?.batch || String(Date.now());

  const desiredLines = [];

  units.forEach((unit, index) => {
    const tier = desiredTiers[index];

    const variantId = PINTRYX_VARIANTS[unit.family]?.[tier];
    if (!variantId) return;

    const order = index + 1;
    const previous = desiredLines[desiredLines.length - 1];

    /*
     * Only combine consecutive identical units.
     * This keeps cart lines compact without losing their logical position.
     */
    if (
      previous &&
      previous.id === variantId &&
      previous.tier === tier &&
      previous.order + previous.quantity === order
    ) {
      previous.quantity += 1;
    } else {
      desiredLines.push({
        id: variantId,
        quantity: 1,
        tier,
        order,
      });
    }
  });

  /*
   * If the cart already has the exact correct tiers, order and batch,
   * don't unnecessarily remove/re-add the lines.
   */
  const alreadyNormalized = units.every((unit, index) => {
    const expectedTier = desiredTiers[index];

    return (
      unit.currentTier === expectedTier &&
      unit.order === index + 1 &&
      unit.batch === normalizedBatch
    );
  });

  if (alreadyNormalized) return cart;

  const sectionsValue = sections.join(',');
  const updates = {};

  pairLines.forEach((line) => {
    updates[line.key] = 0;
  });

  const removeResponse = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig('json', {
      body: JSON.stringify({
        updates,
        sections: sectionsValue,
        sections_url: window.location.pathname,
      }),
    })
  );

  const removedCart = await removeResponse.json();

  if (!removeResponse.ok || removedCart.errors) {
    throw new Error(
      removedCart.description ||
      removedCart.errors ||
      'Cart update failed'
    );
  }

  const items = desiredLines.map((line) => {
    const properties = {
      '_Pintryx bundle': 'Bundle to fit every machine',
      '_Bundle type':
        line.tier === 'default'
          ? '1'
          : line.tier === 'two'
            ? '2'
            : '3+',
      '_Bundle pairs': String(totalPairs),
      '_Bundle order': String(line.order),
      '_Bundle batch': normalizedBatch,
      '_Bundle pricing':
        line.tier === 'default'
          ? 'Regular-price pair'
          : 'Discounted pair',
    };

    if (line.tier === 'two') {
      properties['_Bundle route'] = '2-pair bundle';
    }

    if (line.tier === 'three') {
      properties['_Bundle route'] = 'extra pairs';
    }

    return {
      id: Number(line.id),
      quantity: line.quantity,
      properties,
    };
  });

  if (!items.length) return removedCart;

  const addResponse = await fetch(
    Theme.routes.cart_add_url,
    fetchConfig('json', {
      body: JSON.stringify({
        items,
        sections: sectionsValue,
        sections_url: window.location.pathname,
      }),
    })
  );

  const addedCart = await addResponse.json();

  if (!addResponse.ok || addedCart.status || addedCart.errors) {
    throw new Error(
      addedCart.description ||
      addedCart.message ||
      addedCart.errors ||
      'Cart add failed'
    );
  }

  return addedCart;
}

window.PintryxNormalizeCurrentCart = async function (sections = []) {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  const cart = await response.json();
  if (!response.ok || cart.errors) throw new Error(cart.description || cart.errors || 'Could not read cart');
  const normalizedCart = await normalizePintryxPairLines(cart, sections);
  normalizedCart.pintryxItemCount = cart.item_count;
  return normalizedCart;
};

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const drawer = this.closest('cart-drawer-component');
    const wasDrawerOpen = !!drawer?.querySelector('dialog[open]');

    const editedQuantitySelector = this.refs.quantitySelectors[config.line - 1];
    const editedVariantId = Number(editedQuantitySelector?.dataset.variantId);
    const editedPintryxRoute = PINTRYX_VARIANT_LOOKUP.get(editedVariantId);
    const requiresBundleRebuildLoader = Boolean(
      editedPintryxRoute && (config.action === 'clear' || editedPintryxRoute.tier !== 'three')
    );

    this.#disableCartItems(requiresBundleRebuildLoader);

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then(async (responseText) => {
        let parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        parsedResponseText = await normalizePintryxPairLines(parsedResponseText, Array.from(sectionsToUpdate));

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const nextCartItems = newSectionHTML.querySelector('cart-items-component');
        if (wasDrawerOpen && drawer?.isConnected && nextCartItems) {
          morph(this, nextCartItems);
        } else {
          await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const sectionHtml = event.detail.resource?.sections?.[this.sectionId];
      if (!sectionHtml) return;

      const drawer = this.closest('cart-drawer-component');
      const wasDrawerOpen = !!drawer?.querySelector('dialog[open]');
      if (wasDrawerOpen && drawer?.isConnected) {
        const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const nextCartItems = parsedSection.querySelector('cart-items-component');
        if (nextCartItems) {
          morph(this, nextCartItems);
          return;
        }
      }
      morphSection(this.sectionId, sectionHtml);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  /**
   * Prevents overlapping cart edits and optionally shows the full loader when
   * the Pintryx pricing tiers need to be rebuilt.
   * @param {boolean} showLoader
   */
  #disableCartItems(showLoader = false) {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');

    const loader = this.querySelector('[data-cart-update-loader]');
    if (loader instanceof HTMLElement) loader.hidden = !showLoader;
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');

    const loader = this.querySelector('[data-cart-update-loader]');
    if (loader instanceof HTMLElement) loader.hidden = true;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
